import React from 'react';
import { Link } from 'react-router-dom';
import type { Blog, BlogSummary } from '@vastra/types';
import { formatDate, stripHtmlToText, truncate } from '@vastra/utils';

interface BlogCardProps {
  blog: Blog | BlogSummary;
  linkPrefix?: string;
}

const BlogCard: React.FC<BlogCardProps> = ({ blog, linkPrefix = '/blog' }) => (
  <article className="group depth-scene animate-fade-in" aria-label={blog.title}>
    <Link
      to={`${linkPrefix}/${blog.slug}`}
      className="block focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#1f6f66] rounded-sm"
    >
      <div className="depth-card aspect-[4/3] overflow-hidden mb-6">
        {blog.coverImage ? (
          <img
            src={blog.coverImage}
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
            alt={blog.title}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#c5ced4] bg-gradient-to-b from-[#f4f7f9] to-[#e8eef1]">
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
          </div>
        )}
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <time className="text-[10px] font-semibold text-[#8a847a] uppercase tracking-widest" dateTime={blog.publishedAt}>
            {formatDate(blog.publishedAt)}
          </time>
          {blog.tags.length > 0 && (
            <span className="text-[8px] font-semibold bg-[#f4f7f9] text-[#5a6570] px-2 py-0.5 uppercase tracking-widest">
              {blog.tags[0]}
            </span>
          )}
        </div>
        <h3 className="text-lg font-medium serif text-[#14212b] group-hover:text-[#1f6f66] transition-colors leading-tight">
          {blog.title}
        </h3>
        <p className="text-sm text-[#5a6570] leading-relaxed">
          {truncate(stripHtmlToText(blog.excerpt || ('content' in blog ? blog.content : '')), 120)}
        </p>
        <span className="inline-block text-[10px] font-semibold uppercase tracking-widest text-[#14212b] group-hover:translate-x-1 transition-transform">
          Read More →
        </span>
      </div>
    </Link>
  </article>
);

export default BlogCard;
